import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Tiny React & D3 flamegraph tutorial",
  "description": "",
  "published": "2018-06-06T00:00:00.000Z",
  "redirect_from": ["/blog/tiny-react-d3-flamegraph-tutorial/swizec/8440"],
  "categories": "Front End, Livecoding, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><em parentName="p">{`This is a Livecoding Recap – an almost-weekly post about interesting things discovered while livecoding. Usually shorter than 500 words. Often with pictures. Livecoding happens almost `}<strong parentName="em">{`every Sunday at 2pm PDT`}</strong>{` on multiple channels. You should `}<a parentName="em" {...{
          "href": "http://youtube.com/user/theswizec"
        }}>{`subscribe to ￼My Youtube￼ channel`}</a>{`(`}<a parentName="em" {...{
          "href": "https://www.youtube.com/user/theswizec"
        }}>{`https://www.youtube.com/user/theswizec`}</a>{`) to catch me live.`}</em></p>
    <lite-youtube {...{
      "videoid": "eOenovuDHxE",
      "videostartat": "0"
    }}></lite-youtube>
    <p><a parentName="p" {...{
        "href": "http://brendangregg.com/flamegraphs.html"
      }}>{`Flamegraphs`}</a>{` are great. They show you when you've been bad and made the CPU cry.`}</p>
    <p>{`Here is a flamegraph built with React & D3. It shows some stack data I stole from `}<a parentName="p" {...{
        "href": "https://github.com/spiermar/d3-flame-graph/blob/master/examples/stacks.json"
      }}>{`the internet`}</a>{`:`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`This is better. I'm gonna have to spend some time thinking about how to make individual nodes inspectable with a nice animated transition.`}<br parentName="p"></br><br parentName="p"></br>{`I assume that would be a useful feature `}<a parentName="p" {...{
          "href": "https://twitter.com/brian_d_vaughn"
        }}>{`@brian_d_vaughn`}</a>{` ? `}<a parentName="p" {...{
          "href": "https://t.co/7shyFCxyde"
        }}>{`pic.twitter.com/7shyFCxyde`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1003402982552002560"
      }}>{`June 3, 2018`}</a></blockquote>
    <p>{`You can try it out `}<a parentName="p" {...{
        "href": "https://dist-wnnnpntytb.now.sh/"
      }}>{`here`}</a>{`, and see the `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-d3-flamegraph"
      }}>{`code on GitHub`}</a>{`. Consider it a work in progress. Releasing as open source once the animation works smoothly :)`}</p>
    <h2 {...{
      "id": "heres-how-it-works-"
    }}>{`Here's how it works 👇`}</h2>
    <p>{`You have two components:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`<Flamegraph>`}</inlineCode>{`, which recursively renders the data tree`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`<FlameRect>`}</inlineCode>{`, which renders a particular rectangle and its label`}</li>
    </ol>
    <h3 {...{
      "id": "flamegraph"
    }}>{`Flamegraph`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`<Flamegraph>`}</inlineCode>{` component without animation takes your data, loops it through the array of entries at this level, and calls itself on `}<inlineCode parentName="p">{`data.children`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { scaleLinear, schemeOrRd, color as d3color } from "d3";

class Flamegraph extends React.Component {
    render() {
        const { x = 0, y = 0, width, level = 0, data } = this.props;

        const xScale = scaleLinear()
            .domain([0, data.reduce((sum, d) => sum + d.value, 0)])
            .range([0, width]);

        return (
            <g transform="{\`translate(\${x}," \${y})\`}="">
                {data.map((d, i) => {
                    const start = data
                        .slice(0, i)
                        .reduce((sum, d) => sum + d.value, 0);

                    return (
                        <react class="fragment" key={\`\${level}-\${d.name}\`}>
                            <flamerect x={xScale(start)} y={0} width={xScale(d.value)} height={RowHeight} name={d.name}>
                            {d.children && (
                                <flamegraph data={d.children} x={xScale(start)} y={RowHeight} width={xScale(d.value)} level="{level" +="" 1}="">
                            )}
                        </flamegraph></flamerect></react>
                    );
                })}
            </g>
        );
    }
}
`}</code></pre>
    <p>{`Our `}<inlineCode parentName="p">{`render`}</inlineCode>{` method takes a bunch of params out of `}<inlineCode parentName="p">{`props`}</inlineCode>{`, creates a linear D3 scale to make calculations easier, then renders an SVG grouping element.`}</p>
    <p>{`Inside that element, we loop through the `}<inlineCode parentName="p">{`data`}</inlineCode>{`, and for each entry, we create a new `}<inlineCode parentName="p">{`<React.Fragment>`}</inlineCode>{`. The fragment contains `}<inlineCode parentName="p">{`<FlameRect>`}</inlineCode>{` which represents the current datapoint, and a `}<inlineCode parentName="p">{`<Flamegraph>`}</inlineCode>{` which renders all the child nodes.`}</p>
    <p>{`We decide each element's `}<inlineCode parentName="p">{`x`}</inlineCode>{` position based on the sum of all node values up to the current one. And we make sure the child `}<inlineCode parentName="p">{`<Flamegraph>`}</inlineCode>{` uses the same width as the current node. This creates the neat stacking effect.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/06/flamegraph-screenshot.png",
        "alt": null
      }}></img></p>
    <h3 {...{
      "id": ""
    }}></h3>
    <p>{`The `}<inlineCode parentName="p">{`<FlameRect>`}</inlineCode>{` component takes care of choosing a random color on initial render, highlighting when clicked, and displaying a label if there's enough space.`}</p>
    <p>{`It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import { scaleLinear, schemeOrRd, color as d3color } from "d3";

class FlameRect extends React.Component {
    state = {
        hideLabel: false,
        selected: false,
        color: schemeOrRd[9][Math.floor(Math.random() * 9)]
    };

    labelRefCallback = element => {
        if (
            element &&
            element.getBoundingClientRect().width > this.props.width
        ) {
            this.setState({
                hideLabel: true
            });
        }
    };

    onClick = () => this.setState({ selected: !this.state.selected });

    render() {
        const { x, y, width, height, name } = this.props,
            { hideLabel, selected } = this.state;
        let { color } = this.state;

        if (selected) {
            color = d3color(color).brighter();
        }

        return (
            <g transform="{\`translate(\${x}," \${y})\`}="" style={{ cursor: "pointer" }} onclick={this.onClick}>
                <rect x={0} y={0} width={width} height={height} style={{ stroke: "white", fill: color }}>
                {!hideLabel && (
                    <text x={5} y={13} style={{ fontsize: "12px" }} ref={this.labelRefCallback}>
                        {name}
                    </text>
                )}
            </rect></g>
        );
    }
}
`}</code></pre>
    <p>{`We render a grouping element that contains a `}<inlineCode parentName="p">{`<rect>`}</inlineCode>{` and a `}<inlineCode parentName="p">{`<text>`}</inlineCode>{`. The rect gets sizing and color information, and the label gets a ref callback and some text.`}</p>
    <p>{`We use the ref callback to dynamically detect the size of our label and hide it if necessary. That happens in `}<inlineCode parentName="p">{`labelRefCallback`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`onClick`}</inlineCode>{`, we flip the selected state.`}</p>
    <p>{`And well, that's it. You can think of this component as a basic toggle component. Uses all the same logic, just renders as a colorful rectangle instead of a button.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`That's the basic `}<inlineCode parentName="p">{`<Flamegraph>`}</inlineCode>{` component. Uses recursion to render a tree data structure and lets you highlight individual elements.`}</p>
    <p>{`Where it gets tricky is adding animation that lets users explore their data. Turns out deeply nested React elements are hard to animate smoothly.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Smoothly animating recursive structures is hard. `}<a parentName="p" {...{
          "href": "https://twitter.com/brian_d_vaughn"
        }}>{`@brian_d_vaughn`}</a>{` might not get the flamegraph shininess I half promised`}<a parentName="p" {...{
          "href": "https://twitter.com/sxywu"
        }}>{`@sxywu`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/micahstubbs"
        }}>{`@micahstubbs`}</a>{` any tips? `}<a parentName="p" {...{
          "href": "https://t.co/2ARgb3izCq"
        }}>{`pic.twitter.com/2ARgb3izCq`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1003904212755857408"
      }}>{`June 5, 2018`}</a></blockquote>
    <p>{`The worst part is how long it takes before React even propagates `}<inlineCode parentName="p">{`prop`}</inlineCode>{` updates through the tree before the animation even starts. Once the animation is running, it's smooth as silk.`}</p>
    <p>{`But getting it started, ho boy.`}</p>
    <p>{`This warrants further research. I'll be back :)`}</p>
    <lite-youtube {...{
      "videoid": "MLchhOfiOwM",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`PS: version `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/react-d3-flamegraph"
      }}>{`0.1.0 is in fact on npm`}</a>{` if you want to play.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      